import app from 'flarum/common/app';
import extractText from 'flarum/common/utils/extractText';

/**
 * Age of one kid on its own, e.g. "8 aylık" or "3 yaş".
 *
 * Under two years the month count is what parents actually use; past that,
 * whole years read better.
 *
 * @param {number|null} months
 * @returns {string|null} null when the age cannot be determined.
 */
export function formatKidAge(months) {
  if (typeof months !== 'number' || months < 0) return null;

  if (months < 24) {
    return extractText(app.translator.trans('happydigital-uinfo.lib.age_months', { count: months }));
  }

  return extractText(app.translator.trans('happydigital-uinfo.lib.age_years', { count: Math.floor(months / 12) }));
}

/**
 * Join fragments the way the language lists them: "2, 3 ve 4".
 *
 * @param {Array<string|number>} parts
 * @returns {string}
 */
function joinAges(parts) {
  if (parts.length === 1) return String(parts[0]);

  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1);

  return extractText(
    app.translator.trans('happydigital-uinfo.lib.age_join', {
      list: rest.join(', '),
      last,
    })
  );
}

/**
 * The parent label shown next to a username and in the profile hero, e.g.
 * "2 yaş annesi" or "8 aylık ve 3 yaş annesi".
 *
 * When every kid is measured in the same unit the unit is stated once at the
 * end ("2 ve 4 yaş annesi") rather than repeated per child ("2 yaş ve 4 yaş
 * annesi"), which is how a parent would actually say it. Mixed units fall back
 * to spelling each one out, since "8 ve 3 yaş" would misstate the baby's age.
 *
 * Kids whose age is unknown are skipped rather than rendered as a gap: a
 * partial list still reads correctly, an empty one suppresses the label
 * entirely.
 *
 * @param {Array} kids
 * @returns {string}
 */
export function formatParentLabel(kids) {
  if (!kids || !kids.length) return '';

  const known = kids.filter((kid) => typeof kid.months === 'number' && kid.months >= 0);

  if (!known.length) return '';

  const inMonths = known.filter((kid) => kid.months < 24);
  let ages;

  if (inMonths.length === 0) {
    // All in years: "2, 3 ve 4" + the unit supplied by parent_of_years.
    ages = joinAges(known.map((kid) => Math.floor(kid.months / 12)));

    return app.translator.trans('happydigital-uinfo.lib.parent_of_years', { ages });
  }

  if (inMonths.length === known.length) {
    ages = joinAges(known.map((kid) => kid.months));

    return app.translator.trans('happydigital-uinfo.lib.parent_of_months', { ages });
  }

  // Mixed units: each age carries its own.
  ages = joinAges(known.map((kid) => formatKidAge(kid.months)));

  return app.translator.trans('happydigital-uinfo.lib.parent_of', { ages });
}

/**
 * One kid's full label for the name+age display mode, e.g. "Ada · 3 yaş".
 *
 * @param {{name: string, months: number|null}} kid
 * @returns {string}
 */
export function formatKid(kid) {
  const age = formatKidAge(kid.months) || app.translator.trans('happydigital-uinfo.lib.age_unknown');

  if (kid.name) {
    return app.translator.trans('happydigital-uinfo.lib.kid_with_name', { name: kid.name, age });
  }

  return age;
}
